import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/today', { replace: true });
  }, [user, navigate]);

  // Show spinner while checking auth — prevents flash
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-6 text-center text-sm text-slate-400 max-w-xs leading-relaxed">
          Przygotowujemy PromptOrigin dla Ciebie. Ze względu na złożoność platformy ładowanie może potrwać chwilę.
        </p>
      </div>
    );
  }

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password);
      }
      toast.success(isLogin ? 'Zalogowano!' : 'Konto utworzone!');
      navigate('/today');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wystąpił błąd');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-12" style={{ background: 'transparent' }}>
      <button onClick={() => navigate('/welcome')} className="self-start mb-8 text-slate-400 hover:text-white transition-colors">
        <ArrowLeft className="w-6 h-6" />
      </button>
      
      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isLogin ? 'Witaj ponownie!' : 'Stwórz konto'}
          </h1>
          <p className="text-slate-400">
            {isLogin ? 'Zaloguj się aby kontynuować naukę' : 'Rozpocznij swoją przygodę z AI'}
          </p>
        </div>
        
        <div 
          className="rounded-2xl p-6"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 rounded-xl text-base text-white placeholder:text-slate-500"
              style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
              required
              data-testid="auth-email"
            />
            <Input
              type="password"
              placeholder="Hasło"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-14 rounded-xl text-base text-white placeholder:text-slate-500"
              style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
              required
              minLength={6}
              data-testid="auth-password"
            />
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-lg font-semibold btn-press"
              data-testid="auth-submit"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                isLogin ? 'Zaloguj się' : 'Zarejestruj się'
              )}
            </Button>
          </form>
        </div>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-sky-400 font-medium hover:text-sky-300 transition-colors"
            data-testid="auth-toggle"
          >
            {isLogin ? 'Nie masz konta? Zarejestruj się' : 'Masz już konto? Zaloguj się'}
          </button>
        </div>
      </div>
    </div>
  );
};
